// src/pages/Reviews.jsx
import React, { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { useTenant } from "../context/TenantContext";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import {
  FiStar,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiX,
  FiCalendar,
  FiTag,
} from "react-icons/fi";
import api from "../services/api";
import "../styles/reviews-page.css";

const Reviews = () => {
  const { tenant } = useTenant();
  const [selectedRating, setSelectedRating] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const reviewsPerPage = 9;
  const [availableCategories, setAvailableCategories] = useState([]);

  // ✅ Use the working /all endpoint
  const {
    data: allReviewsData, // Changed back to allReviewsData
    isLoading,
    error,
  } = useQuery(
    [
      "all-reviews",
      tenant?.TenantID,
      selectedRating,
      selectedCategory,
      selectedTimeframe,
      currentPage,
    ],
    async () => {
      try {
        // Convert timeframe to days
        let timeframeDays = null;
        if (selectedTimeframe) {
          const timeframeMap = {
            week: 7,
            month: 30,
            "3months": 90,
            year: 365,
          };
          timeframeDays = timeframeMap[selectedTimeframe];
        }

        const params = {
          tenantCode: tenant?.Code || tenant?.TenantCode,
          page: currentPage,
          limit: reviewsPerPage,
        };

        if (selectedRating) params.rating = selectedRating;
        if (selectedCategory) params.category = selectedCategory;
        if (timeframeDays) params.timeframe = timeframeDays;

        console.log("🔍 Fetching reviews with params:", params);

        const response = await api.get("api/reviews/all", { params });

        console.log("✅ Reviews response:", response.data);

        // Extract the reviews from the response
        const reviews = response.data?.reviews || [];
        const categories = response.data?.filters?.categories || [];

        // Store categories for filter options
        setAvailableCategories(categories);

        return reviews;
      } catch (error) {
        console.error("❌ Error fetching reviews:", error);
        return [];
      }
    },
    {
      enabled: !!tenant,
      staleTime: 30000,
      keepPreviousData: true,
      retry: 2,
    },
  );

  // Filter reviews based on all criteria (client-side filtering for now)
  const filteredReviews = useMemo(() => {
    if (!allReviewsData || !Array.isArray(allReviewsData)) return [];

    let filtered = [...allReviewsData];

    if (selectedRating) {
      filtered = filtered.filter(
        (review) => review && Math.floor(review.Rating) === selectedRating,
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (review) =>
          review?.categoryId === selectedCategory ||
          review?.category === selectedCategory,
      );
    }

    if (selectedTimeframe) {
      const timeframe = timeframes.find((t) => t.value === selectedTimeframe);
      if (timeframe) {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - timeframe.days);
        filtered = filtered.filter(
          (review) => review && new Date(review.CreatedAt) >= cutoffDate,
        );
      }
    }

    return filtered;
  }, [allReviewsData, selectedRating, selectedCategory, selectedTimeframe]);

  // Pagination
  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);
  const startIndex = (currentPage - 1) * reviewsPerPage;
  const paginatedReviews = filteredReviews.slice(
    startIndex,
    startIndex + reviewsPerPage,
  );

  // Statistics
  const totalReviews = allReviewsData?.length || 0;
  const filteredTotal = filteredReviews.length;
  const averageRating =
    totalReviews > 0
      ? (
          allReviewsData.reduce((sum, r) => sum + (r?.Rating || 0), 0) /
          totalReviews
        ).toFixed(1)
      : 0;

  // Rating distribution
  const ratingDistribution = [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count:
      allReviewsData?.filter((r) => r && Math.floor(r.Rating) === rating)
        .length || 0,
  }));

  // Timeframe options
  const timeframes = [
    { value: "week", label: "Last 7 days", days: 7 },
    { value: "month", label: "Last 30 days", days: 30 },
    { value: "3months", label: "Last 3 months", days: 90 },
    { value: "year", label: "Last year", days: 365 },
  ];

  // Handle filters
  const handleRatingFilter = (rating) => {
    setSelectedRating(selectedRating === rating ? null : rating);
    setCurrentPage(1);
  };

  const handleCategoryFilter = (categoryId) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
    setCurrentPage(1);
  };

  const handleTimeframeFilter = (timeframe) => {
    setSelectedTimeframe(selectedTimeframe === timeframe ? null : timeframe);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedRating(null);
    setSelectedCategory(null);
    setSelectedTimeframe(null);
    setCurrentPage(1);
  };

  const hasActiveFilters =
    selectedRating || selectedCategory || selectedTimeframe;

  const renderStars = (rating, size = "small") => {
    const starSize = size === "large" ? 20 : 16;
    const roundedRating = Math.round(rating || 0);
    return (
      <div className="review-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            size={starSize}
            className={`review-star ${star <= roundedRating ? "filled" : ""}`}
          />
        ))}
      </div>
    );
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.charAt(0).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      "#2c7da0",
      "#61a5c2",
      "#89c2d9",
      "#2a9d8f",
      "#e9c46a",
      "#f4a261",
      "#e76f51",
    ];
    const index = (name?.length || 0) % colors.length;
    return colors[index];
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Loading state
  if (isLoading) return <LoadingSpinner />;

  // Error state
  if (error) {
    return (
      <div className="reviews-page">
        <div className="reviews-container">
          <div className="error-message">
            <h3>Error loading reviews</h3>
            <p>Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reviews-page">
      <div className="reviews-container">
        <div className="reviews-header">
          <h1>Customer Reviews</h1>
          <p>What our customers say about their shopping experience</p>
        </div>

        <div className="reviews-main-layout">
          {/* Left Sidebar - Filters */}
          <div
            className={`reviews-sidebar ${showMobileFilters ? "active" : ""}`}
          >
            {/* Rating Filter */}
            <div className="sidebar-filter-group">
              <h4>
                <FiStar /> Filter by Rating
              </h4>
              <div className="sidebar-rating-options">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count =
                    ratingDistribution.find((r) => r.rating === rating)
                      ?.count || 0;
                  return (
                    <button
                      key={rating}
                      className={`sidebar-rating-btn ${selectedRating === rating ? "active" : ""}`}
                      onClick={() => handleRatingFilter(rating)}
                    >
                      <div className="rating-stars-small">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FiStar
                            key={star}
                            size={14}
                            className={`small-star ${star <= rating ? "filled" : ""}`}
                          />
                        ))}
                      </div>
                      <span className="rating-count">({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Category Filter */}
            {availableCategories.length > 0 && (
              <div className="sidebar-filter-group">
                <h4>
                  <FiTag /> Filter by Category
                </h4>
                <div className="sidebar-category-options">
                  {availableCategories.map((category) => (
                    <button
                      key={category.id}
                      className={`sidebar-category-btn ${selectedCategory === category.id ? "active" : ""}`}
                      onClick={() => handleCategoryFilter(category.id)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Timeframe Filter */}
            <div className="sidebar-filter-group">
              <h4>
                <FiCalendar /> Filter by Date
              </h4>
              <div className="sidebar-timeframe-options">
                {timeframes.map((tf) => (
                  <button
                    key={tf.value}
                    className={`sidebar-timeframe-btn ${selectedTimeframe === tf.value ? "active" : ""}`}
                    onClick={() => handleTimeframeFilter(tf.value)}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Reset Filters Button */}
            {hasActiveFilters && (
              <button className="sidebar-reset-btn" onClick={clearFilters}>
                Clear All Filters
              </button>
            )}
          </div>

          {/* Right Content - Stats and Reviews */}
          <div className="reviews-content">
            {/* Stats Section */}
            <div className="reviews-stats-section">
              <div className="reviews-stats-grid">
                <div className="stats-card">
                  <div className="stats-rating">
                    <span className="stats-rating-number">{averageRating}</span>
                    {renderStars(Number(averageRating), "large")}
                  </div>
                  <div className="stats-total">
                    Based on {totalReviews} customer{" "}
                    {totalReviews === 1 ? "review" : "reviews"}
                  </div>
                </div>

                <div className="stats-distribution">
                  {ratingDistribution.map((item) => (
                    <div
                      key={item.rating}
                      className={`distribution-bar ${selectedRating === item.rating ? "active" : ""}`}
                      onClick={() => handleRatingFilter(item.rating)}
                    >
                      <span className="distribution-label">
                        {item.rating} ★
                      </span>
                      <div className="distribution-bar-bg">
                        <div
                          className="distribution-bar-fill"
                          style={{
                            width:
                              totalReviews > 0
                                ? `${(item.count / totalReviews) * 100}%`
                                : "0%",
                          }}
                        />
                      </div>
                      <span className="distribution-count">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Filter Toggle */}
            <div className="reviews-filter-bar">
              <button
                className="filter-toggle"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
              >
                <FiFilter /> Filters
              </button>
              <div className="filter-right">
                <span>
                  Showing {filteredTotal}{" "}
                  {filteredTotal === 1 ? "review" : "reviews"}
                </span>
              </div>
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <div className="active-filters-container">
                {selectedRating && (
                  <div className="active-filter">
                    <span>{selectedRating} ★</span>
                    <button onClick={() => handleRatingFilter(selectedRating)}>
                      <FiX />
                    </button>
                  </div>
                )}
                {selectedCategory && (
                  <div className="active-filter">
                    <FiTag size={12} />
                    <span>
                      {availableCategories.find(
                        (c) => c.id === selectedCategory,
                      )?.name || "Category"}
                    </span>
                    <button
                      onClick={() => handleCategoryFilter(selectedCategory)}
                    >
                      <FiX />
                    </button>
                  </div>
                )}
                {selectedTimeframe && (
                  <div className="active-filter">
                    <FiCalendar size={12} />
                    <span>
                      {
                        timeframes.find((t) => t.value === selectedTimeframe)
                          ?.label
                      }
                    </span>
                    <button
                      onClick={() => handleTimeframeFilter(selectedTimeframe)}
                    >
                      <FiX />
                    </button>
                  </div>
                )}
                <button className="clear-all-filters" onClick={clearFilters}>
                  Clear all
                </button>
              </div>
            )}

            {/* Reviews Grid */}
            {paginatedReviews?.length === 0 ? (
              <div className="no-reviews-found">
                <div className="no-reviews-icon">📝</div>
                <h3>No reviews found</h3>
                <p>
                  {hasActiveFilters
                    ? "No reviews match your filters. Try adjusting them!"
                    : "Be the first to write a review!"}
                </p>
                {hasActiveFilters ? (
                  <button className="clear-filters-btn" onClick={clearFilters}>
                    Clear Filters
                  </button>
                ) : (
                  <Link to="/shop" className="shop-now-btn">
                    Shop Now
                  </Link>
                )}
              </div>
            ) : (
              <div className="reviews-grid">
                {paginatedReviews?.map((review, index) => (
                  <div
                    key={review?.ReviewID || `review-${index}`}
                    className="review-card-full"
                  >
                    <div className="review-card-header">
                      <div className="reviewer-avatar">
                        <div
                          className="avatar-circle"
                          style={{
                            background: getAvatarColor(review?.ReviewerName),
                          }}
                        >
                          {getInitials(review?.ReviewerName)}
                        </div>
                        <div className="reviewer-details">
                          <h4>{review?.ReviewerName || "Verified Customer"}</h4>
                          <div className="review-date">
                            <FiClock size={12} />
                            {review?.CreatedAt
                              ? new Date(review.CreatedAt).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  },
                                )
                              : "Unknown date"}
                          </div>
                        </div>
                      </div>
                      <div className="review-rating-section">
                        {renderStars(review?.Rating || 0)}
                      </div>
                    </div>

                    <div className="review-card-body">
                      <p className="review-message">
                        "{review?.Comment || "No comment provided"}"
                      </p>
                    </div>

                    <div className="review-card-footer">
                      <div className="review-product-info">
                        <div className="review-category-badge">
                          {review?.category || "Uncategorized"}
                        </div>
                        <Link
                          to={`/product/${review?.productId}`}
                          className="review-product-link"
                        >
                          <img
                            src={
                              review?.productImage || "/images/placeholder.jpg"
                            }
                            alt={review?.productName || "Product"}
                            className="review-product-thumb"
                            onError={(e) => {
                              e.target.src = "/images/placeholder.jpg";
                            }}
                          />
                          <span>
                            {review?.productName || "Unknown Product"}
                          </span>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 1}
                  onClick={() => goToPage(currentPage - 1)}
                >
                  <FiChevronLeft /> Previous
                </button>
                <div className="pagination-pages">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      className={`pagination-page ${currentPage === i + 1 ? "active" : ""}`}
                      onClick={() => goToPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  className="pagination-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => goToPage(currentPage + 1)}
                >
                  Next <FiChevronRight />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reviews;
